/**
 * Shift Generator - TypeScript port of Python shift_generator.py
 * 
 * Generates optimal shift patterns for on-demand transit services
 * based on service requirements, break constraints, and peak periods.
 */

import { GeneratorConfig, GeneratedShift, BreakSchedule } from './types';
import { OperatingPeriod } from '../../../../types/simulation';
import {
  parseTime,
  formatTime,
  roundToNearest15,
  calculateTimeDifference,
  addMinutes,
  isTimeInRange,
} from './timeUtils';

/**
 * Shift Generator Class
 * Ports the Python ShiftPatternGenerator to TypeScript
 */
export class ShiftGenerator {
  // Configuration
  private serviceStartMinutes: number;
  private serviceEndMinutes: number;
  private maxShiftTimeMinutes: number;
  private breakLengthMinutes: number;
  private minTimeBeforeBreakMinutes: number;
  private minTimeAfterBreakMinutes: number;
  private numVehicles: number;
  private breakReductionDuringPeak: number;
  private staggerBreaks: boolean;
  private demandCurve: Record<number, number>;
  private hourlyVehicleRequirements: Record<number, number>;
  private useDemandBasedAllocation: boolean;
  private peakPeriods: Array<{ startMinutes: number; endMinutes: number }>;

  constructor(config: GeneratorConfig) {
    // Parse times to minutes
    this.serviceStartMinutes = parseTime(config.serviceStartTime);
    this.serviceEndMinutes = parseTime(config.serviceEndTime);
    
    // Shift and break configuration
    this.maxShiftTimeMinutes = config.maxShiftTimeMinutes;
    this.breakLengthMinutes = config.breakLengthMinutes;
    this.minTimeBeforeBreakMinutes = config.minTimeBeforeBreakMinutes;
    this.minTimeAfterBreakMinutes = config.minTimeAfterBreakMinutes;
    
    // Fleet configuration
    const baseVehicles = config.mode === 'trip-layer' 
      ? (config.manualVehicleCount || 1)
      : (config.manualVehicleCount || 1);
    
    // Apply flexibility adjustment
    const flexibilityMultiplier = 1 + (config.flexibilityPercent / 100);
    this.numVehicles = Math.max(1, Math.round(baseVehicles * flexibilityMultiplier));
    
    // Advanced options
    this.breakReductionDuringPeak = config.breakReductionDuringPeakMinutes;
    this.staggerBreaks = config.staggerBreaks;
    this.demandCurve = config.demandCurve || {};
    this.hourlyVehicleRequirements = config.hourlyVehicleRequirements || {};
    this.useDemandBasedAllocation = config.useDemandBasedAllocation;
    
    // Parse peak periods
    this.peakPeriods = config.peakPeriods.map(peak => ({
      startMinutes: parseTime(peak.start),
      endMinutes: parseTime(peak.end),
    }));
  }

  /**
   * Check if a time falls within any peak period
   */
  private isPeakPeriod(timeMinutes: number): boolean {
    for (const peak of this.peakPeriods) {
      if (isTimeInRange(timeMinutes, peak.startMinutes, peak.endMinutes)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calculate the number of breaks required for a shift
   */
  private calculateRequiredBreaks(shiftDuration: number, shiftStart: number): number {
    // If shift is too short for breaks
    if (shiftDuration <= this.minTimeBeforeBreakMinutes + this.minTimeAfterBreakMinutes) {
      return 0;
    }

    // Determine break length (may be reduced during peak)
    let breakLen = this.breakLengthMinutes;
    if (this.isPeakPeriod(shiftStart) && this.breakReductionDuringPeak > 0) {
      breakLen = Math.max(0, this.breakLengthMinutes - this.breakReductionDuringPeak);
    }

    if (breakLen === 0) {
      return 0;
    }

    // Calculate available time for breaks
    const effectiveDuration = shiftDuration - (this.minTimeBeforeBreakMinutes + this.minTimeAfterBreakMinutes);
    if (effectiveDuration <= 0) {
      return 0;
    }

    // Start with 1 break
    let breaksNeeded = 1;
    let remainingTime = effectiveDuration - breakLen;

    // Add more breaks if shift is very long
    while (remainingTime > this.maxShiftTimeMinutes) {
      breaksNeeded++;
      remainingTime -= breakLen;
    }

    return breaksNeeded;
  }

  /**
   * Schedule breaks within a shift
   * Returns array of break schedules with start/end times in minutes
   */
  private scheduleBreaks(
    shiftStart: number,
    shiftEnd: number,
    numBreaks: number
  ): BreakSchedule[] {
    if (numBreaks === 0) {
      return [];
    }

    const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
    const availableTime = shiftDuration - (this.minTimeBeforeBreakMinutes + this.minTimeAfterBreakMinutes);

    // Determine break length
    let breakLen = this.breakLengthMinutes;
    if (this.isPeakPeriod(shiftStart) && this.breakReductionDuringPeak > 0) {
      breakLen = Math.max(0, this.breakLengthMinutes - this.breakReductionDuringPeak);
    }

    if (breakLen === 0) {
      return [];
    }

    const breaks: BreakSchedule[] = [];

    if (numBreaks === 1) {
      // Single break: place at midpoint
      const breakStart = addMinutes(
        shiftStart,
        this.minTimeBeforeBreakMinutes + Math.floor(availableTime / 2)
      );
      breaks.push({
        startMinutes: breakStart,
        endMinutes: addMinutes(breakStart, breakLen),
        durationMinutes: breakLen,
      });
    } else {
      // Multiple breaks: distribute evenly
      const interval = (availableTime - (numBreaks * breakLen)) / (numBreaks + 1);

      for (let i = 0; i < numBreaks; i++) {
        const breakStart = addMinutes(
          shiftStart,
          this.minTimeBeforeBreakMinutes + Math.floor((i + 1) * interval) + i * breakLen
        );
        breaks.push({
          startMinutes: breakStart,
          endMinutes: addMinutes(breakStart, breakLen),
          durationMinutes: breakLen,
        });
      }
    }

    return breaks;
  }

  /**
   * Find staggered break times to avoid all vehicles breaking simultaneously
   */
  private findStaggeredBreakTimes(
    shiftStart: number,
    shiftDuration: number,
    vehicleIndex: number
  ): BreakSchedule[] {
    const shiftEnd = addMinutes(shiftStart, shiftDuration);
    
    // Calculate how many breaks are needed
    const numBreaksNeeded = this.calculateRequiredBreaks(shiftDuration, shiftStart);
    
    if (numBreaksNeeded === 0) {
      return [];
    }

    // Calculate the optimal break window (middle portion of shift)
    const availableBreakWindow = shiftDuration - this.minTimeBeforeBreakMinutes - this.minTimeAfterBreakMinutes;
    
    if (availableBreakWindow < this.breakLengthMinutes) {
      // Not enough time for a break, return empty
      return [];
    }

    // Find potential break windows with finer granularity (every 30 minutes)
    const breakOptions: BreakSchedule[] = [];
    let currentTime = addMinutes(shiftStart, this.minTimeBeforeBreakMinutes);
    const breakWindowEnd = addMinutes(shiftEnd, -this.minTimeAfterBreakMinutes - this.breakLengthMinutes);
    
    while (currentTime <= breakWindowEnd) {
      const potentialBreakStart = currentTime;
      const potentialBreakEnd = addMinutes(potentialBreakStart, this.breakLengthMinutes);
      
      // Check if break doesn't overlap with peak periods (if peak periods are defined)
      const notInPeak = this.peakPeriods.length === 0 || 
                        (!this.isPeakPeriod(potentialBreakStart) && 
                         !this.isPeakPeriod(potentialBreakEnd));
      
      if (notInPeak) {
        const roundedStart = roundToNearest15(potentialBreakStart);
        breakOptions.push({
          startMinutes: roundedStart,
          endMinutes: addMinutes(roundedStart, this.breakLengthMinutes), // Use exact break length
          durationMinutes: this.breakLengthMinutes,
        });
      }

      // Move to next 30-minute interval for better staggering
      currentTime = addMinutes(currentTime, 30);
    }

    // If no non-peak options found, create options even during peaks
    if (breakOptions.length === 0) {
      currentTime = addMinutes(shiftStart, this.minTimeBeforeBreakMinutes);
      while (currentTime <= breakWindowEnd) {
        const roundedStart = roundToNearest15(currentTime);
        breakOptions.push({
          startMinutes: roundedStart,
          endMinutes: addMinutes(roundedStart, this.breakLengthMinutes), // Use exact break length
          durationMinutes: this.breakLengthMinutes,
        });
        currentTime = addMinutes(currentTime, 30);
      }
    }

    // Stagger breaks by vehicle index
    if (breakOptions.length > 0) {
      const breaks: BreakSchedule[] = [];
      
      // For multiple breaks, distribute them across the shift
      if (numBreaksNeeded === 1) {
        const chosenIndex = vehicleIndex % breakOptions.length;
        breaks.push(breakOptions[chosenIndex]);
      } else {
        // For multiple breaks, space them out and stagger by vehicle
        const spacing = Math.floor(breakOptions.length / (numBreaksNeeded + 1));
        for (let i = 0; i < numBreaksNeeded; i++) {
          const baseIndex = (i + 1) * spacing;
          const offset = vehicleIndex * 2; // Stagger by 2 positions per vehicle
          const chosenIndex = (baseIndex + offset) % breakOptions.length;
          breaks.push(breakOptions[chosenIndex]);
        }
      }
      
      return breaks;
    }

    // Fallback to regular scheduling if no options
    return this.scheduleBreaks(shiftStart, shiftEnd, numBreaksNeeded);
  }

  /**
   * Get adjusted vehicle count based on demand curve
   */
  private adjustForDemand(timeMinutes: number): number {
    const hour = Math.floor(timeMinutes / 60);
    return this.demandCurve[hour] || this.numVehicles;
  }

  /**
   * Generate demand-based shifts that match hourly vehicle requirements
   * Reduces active vehicles during low-demand periods
   */
  private generateDemandBasedShifts(): GeneratedShift[] {
    const shifts: GeneratedShift[] = [];
    const serviceStartHour = Math.floor(this.serviceStartMinutes / 60);
    const serviceEndHour = Math.ceil(this.serviceEndMinutes / 60);
    
    // Build vehicle assignments: each vehicle may have multiple disjoint active segments.
    const vehicleAssignments: Array<{ vehicleNum: number; segments: Array<{ start: number; end: number }> }> = [];
    
    // Assign vehicles based on demand priority
    // Vehicle 1 covers full service, Vehicle 2 covers most hours, etc.
    for (let vehicleNum = 1; vehicleNum <= this.numVehicles; vehicleNum++) {
      const activeHours: number[] = [];
      
      // Find which hours this vehicle needs to cover
      for (let hour = serviceStartHour; hour < serviceEndHour; hour++) {
        const requiredVehicles = this.hourlyVehicleRequirements[hour] || 0;
        
        // This vehicle is needed if it's within the top N vehicles needed
        if (vehicleNum <= requiredVehicles) {
          activeHours.push(hour);
        }
      }

      if (activeHours.length === 0) {
        continue;
      }

      const segments: Array<{ start: number; end: number }> = [];
      let segmentStartHour = activeHours[0];
      let previousHour = activeHours[0];

      for (let i = 1; i < activeHours.length; i++) {
        const hour = activeHours[i];
        if (hour === previousHour + 1) {
          previousHour = hour;
          continue;
        }
        segments.push({
          start: Math.max(this.serviceStartMinutes, segmentStartHour * 60),
          end: Math.min(this.serviceEndMinutes, (previousHour + 1) * 60),
        });
        segmentStartHour = hour;
        previousHour = hour;
      }

      segments.push({
        start: Math.max(this.serviceStartMinutes, segmentStartHour * 60),
        end: Math.min(this.serviceEndMinutes, (previousHour + 1) * 60),
      });

      const validSegments = segments.filter((segment) => segment.end > segment.start);
      if (validSegments.length > 0) {
        vehicleAssignments.push({
          vehicleNum,
          segments: validSegments,
        });
      }
    }
    
    // Generate shifts based on assignments
    vehicleAssignments.forEach((assignment, index) => {
      const operatingPeriods: OperatingPeriod[] = [];

      assignment.segments.forEach((segment) => {
        const segmentDuration = calculateTimeDifference(segment.start, segment.end);
        if (segmentDuration <= this.maxShiftTimeMinutes) {
          operatingPeriods.push(this.generateOperatingPeriod(segment.start, segment.end, index));
          return;
        }

        let currentStart = segment.start;
        while (currentStart < segment.end) {
          const remainingTime = calculateTimeDifference(currentStart, segment.end);
          const splitDuration = Math.min(this.maxShiftTimeMinutes, remainingTime);
          const splitEnd = addMinutes(currentStart, splitDuration);
          if (splitEnd <= currentStart) {
            break;
          }
          operatingPeriods.push(this.generateOperatingPeriod(currentStart, splitEnd, index));
          currentStart = splitEnd;
        }
      });

      if (operatingPeriods.length > 0) {
        shifts.push({
          vehicleId: `V${assignment.vehicleNum.toString().padStart(2, '0')}`,
          vehicleName: `Auto Vehicle ${assignment.vehicleNum}`,
          operatingPeriods,
        });
      }
    });
    
    return shifts;
  }

  /**
   * Main method: Generate shift patterns for all vehicles
   * Returns array of generated shifts
   */
  public generateShifts(): GeneratedShift[] {
    // Use demand-based allocation if enabled and hourly requirements are available
    if (this.useDemandBasedAllocation && Object.keys(this.hourlyVehicleRequirements).length > 0) {
      return this.generateDemandBasedShifts();
    }

    const shifts: GeneratedShift[] = [];
    const serviceDuration = calculateTimeDifference(this.serviceStartMinutes, this.serviceEndMinutes);

    // Check if we need multiple shifts per vehicle
    const needsMultipleShifts = serviceDuration > this.maxShiftTimeMinutes;

    if (!needsMultipleShifts) {
      // Simple case: single shift covers entire service
      for (let vehicleNum = 1; vehicleNum <= this.numVehicles; vehicleNum++) {
        const shift = this.generateSingleShift(
          vehicleNum,
          this.serviceStartMinutes,
          this.serviceEndMinutes,
          vehicleNum - 1
        );
        shifts.push(shift);
      }
    } else {
      // Complex case: multiple shifts needed per vehicle
      const shiftsPerVehicle = Math.ceil(serviceDuration / this.maxShiftTimeMinutes);
      
      for (let vehicleNum = 1; vehicleNum <= this.numVehicles; vehicleNum++) {
        const operatingPeriods: OperatingPeriod[] = [];
        let currentStart = this.serviceStartMinutes;
        
        for (let shiftNum = 0; shiftNum < shiftsPerVehicle; shiftNum++) {
          const remainingTime = calculateTimeDifference(currentStart, this.serviceEndMinutes);
          const shiftDuration = Math.min(this.maxShiftTimeMinutes, remainingTime);
          const shiftEnd = addMinutes(currentStart, shiftDuration);

          // Only create operating period if it's long enough
          if (shiftDuration >= this.minTimeBeforeBreakMinutes + this.minTimeAfterBreakMinutes) {
            const operatingPeriod = this.generateOperatingPeriod(
              currentStart,
              shiftEnd,
              vehicleNum - 1
            );
            operatingPeriods.push(operatingPeriod);
          }

          currentStart = shiftEnd;
          
          // Stop if we've reached service end
          if (currentStart === this.serviceEndMinutes) {
            break;
          }
        }

        // Create single GeneratedShift with all operating periods for this vehicle
        if (operatingPeriods.length > 0) {
          shifts.push({
            vehicleId: `V${vehicleNum.toString().padStart(2, '0')}`,
            vehicleName: `Auto Vehicle ${vehicleNum}`,
            operatingPeriods,
          });
        }
      }
    }

    return shifts;
  }

  /**
   * Generate a single operating period
   */
  private generateOperatingPeriod(
    shiftStart: number,
    shiftEnd: number,
    vehicleIndex: number
  ): OperatingPeriod {
    // Round times
    const roundedStart = roundToNearest15(shiftStart);
    const roundedEnd = roundToNearest15(shiftEnd);
    const shiftDuration = calculateTimeDifference(roundedStart, roundedEnd);

    // Calculate and schedule breaks
    let breakSchedules: BreakSchedule[];
    if (this.staggerBreaks) {
      // Use staggered breaks to distribute breaks across vehicles
      breakSchedules = this.findStaggeredBreakTimes(roundedStart, shiftDuration, vehicleIndex);
    } else {
      // Use standard break scheduling (all vehicles break at same time)
      const numBreaks = this.calculateRequiredBreaks(shiftDuration, roundedStart);
      breakSchedules = this.scheduleBreaks(roundedStart, roundedEnd, numBreaks);
    }

    // Convert to OperatingPeriod format
    return {
      start: formatTime(roundedStart),
      end: formatTime(roundedEnd),
      breaks: breakSchedules.map(brk => ({
        start: formatTime(brk.startMinutes),
        end: formatTime(brk.endMinutes),
        location: '', // No specific break location
      })),
    };
  }

  /**
   * Generate a single shift for one vehicle
   */
  private generateSingleShift(
    vehicleNum: number,
    shiftStart: number,
    shiftEnd: number,
    vehicleIndex: number
  ): GeneratedShift {
    const operatingPeriod = this.generateOperatingPeriod(shiftStart, shiftEnd, vehicleIndex);

    return {
      vehicleId: `V${vehicleNum.toString().padStart(2, '0')}`,
      vehicleName: `Auto Vehicle ${vehicleNum}`,
      operatingPeriods: [operatingPeriod],
    };
  }

  /**
   * Get service duration in minutes
   */
  public getServiceDuration(): number {
    return calculateTimeDifference(this.serviceStartMinutes, this.serviceEndMinutes);
  }

  /**
   * Get total number of vehicles (after flexibility adjustment)
   */
  public getTotalVehicles(): number {
    return this.numVehicles;
  }
}

/**
 * Convenience function to generate shifts from config
 */
export function generateShiftsFromConfig(config: GeneratorConfig): GeneratedShift[] {
  const generator = new ShiftGenerator(config);
  return generator.generateShifts();
}

